import { fetcher } from 'utils/axios';
import { store } from 'store';
import type { ChatUserModel } from 'types/chat-users/chat-user';
import type { ContactResponse } from 'types/contacts/contact-response';
import { normalizePhoneForChatUserApi } from 'api/contacts-messaging';

// =============================================================================
// Types
// =============================================================================

export type UpdateSessionResponse = {
  success: boolean;
  data?: {
    /**
     * Backward-compat flat fields. Populated when ANY of the following
     * triggers an effective state transition:
     *   - flowStatusCode was sent in the request
     *   - status was sent and translated to a flow change
     *   - managerId was sent and the action is not "noop" (manager updates
     *     implicitly move flow to 2 or 1 in first_assign / unassign)
     */
    flowStatusCode?: number;
    flowStatus?: string;
    /** "open" | "closed" — final session.status after all field updates */
    sessionStatus?: 'open' | 'closed';
    /** Final manager_id after the update (string, null for unassign) */
    managerId?: string | null;
    flow?: {
      sessionId: string;
      oldStatusCode: number | null;
      newStatusCode: number;
      statusChanged: boolean;
    };
    /** .NET returns PascalCase when PropertyNamingPolicy is null */
    Flow?: {
      SessionId: string;
      OldStatusCode: number | null;
      NewStatusCode: number;
      StatusChanged: boolean;
    };
    manager?: {
      sessionId: string;
      action: 'noop' | 'first_assign' | 'reassign' | 'unassign' | 'unassign_close';
      newManagerId: string | null;
      newStatusCode: number;
      newStatus: 'open' | 'closed';
    };
    /** Legacy: object returned when "status" was sent (translated to flow change). */
    status?: {
      sessionId: string;
      oldStatusCode: number | null;
      newStatusCode: number;
      statusChanged: boolean;
    };
  };
};

export type SessionFlowLogEventType =
  | 'SESSION_CREATED'
  | 'STATUS_CHANGED'
  | 'MANAGER_CHANGED'
  | 'SESSION_CLOSED'
  | 'SESSION_REOPENED';

export type SessionFlowLogSource =
  | 'webhook_incoming'
  | 'webhook_outgoing'
  | 'auto_assign_default'
  | 'manual_status_change'
  | 'manual_assignee_change'
  | 'manual_unassign_close'
  | 'bot_close'
  | 'cron_fallback'
  | 'backfill';

export type SessionFlowLogEntry = {
  id: string;
  sessionId: string;
  companyId: string;
  eventType: SessionFlowLogEventType;
  oldStatusCode: number | null;
  newStatusCode: number | null;
  oldManagerId: string | null;
  newManagerId: string | null;
  source: SessionFlowLogSource;
  actorUserId: string | null;
  metadataJson: string | null;
  createdAt: string;
};

export type GetSessionsLogQuery = {
  sessionId?: string;
  eventType?: SessionFlowLogEventType;
  source?: SessionFlowLogSource;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

export type GetSessionsLogResponse = {
  success: boolean;
  data: SessionFlowLogEntry[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

/** Normalize PUT /api/sessions response (.NET uses PascalCase JSON). */
export function readSessionPutData(data: UpdateSessionResponse['data'] | undefined | null) {
  if (!data) {
    return {
      flowStatusCode: undefined as number | undefined,
      flowStatus: undefined as string | undefined,
      sessionStatus: undefined as string | undefined,
      managerId: undefined as string | null | undefined,
      statusChanged: undefined as boolean | undefined,
      oldStatusCode: undefined as number | null | undefined,
      newStatusCode: undefined as number | undefined,
      flowSessionId: undefined as string | undefined,
    };
  }
  const raw = data as Record<string, unknown>;
  const flow = (raw.flow ?? raw.Flow) as Record<string, unknown> | undefined;
  return {
    flowStatusCode: (raw.flowStatusCode ?? raw.FlowStatusCode) as number | undefined,
    flowStatus: (raw.flowStatus ?? raw.FlowStatus) as string | undefined,
    sessionStatus: (raw.sessionStatus ?? raw.SessionStatus) as string | undefined,
    managerId: (raw.managerId ?? raw.ManagerId) as string | null | undefined,
    statusChanged: (flow?.statusChanged ?? flow?.StatusChanged) as boolean | undefined,
    oldStatusCode: (flow?.oldStatusCode ?? flow?.OldStatusCode) as number | null | undefined,
    newStatusCode: (flow?.newStatusCode ?? flow?.NewStatusCode) as number | undefined,
    flowSessionId: (flow?.sessionId ?? flow?.SessionId) as string | undefined,
  };
}

// =============================================================================
// Mutations — .NET Server (SessionController PUT + SignalR session:update).
// Log queries below remain on server-nodejs.
// =============================================================================

// =============================================================================
// Session id resolution — Redux first, getContacts only as last resort
// =============================================================================

function phonesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalizePhoneForChatUserApi(a ?? '');
  const nb = normalizePhoneForChatUserApi(b ?? '');
  return Boolean(na && nb && na === nb);
}

/** Read SessionId from Redux for a contact phone (selected chat + list caches). */
export function getSessionIdFromStore(phone: string): string | null {
  const { chat } = store.getState();
  const pick = (u: ChatUserModel | null | undefined): string | null => {
    const id = u?.SessionId?.trim();
    return id || null;
  };

  //* selectedChatUser קודם, אבל בלי early-return על null — משתמש נבחר שנטען
  //* ממקור בלי SessionId (למשל legacy getLegasyByPhone) אסור שיסתיר שורת
  //* רשימה של אותו איש קשר שכן נושאת SessionId.
  if (chat.selectedChatUser && phonesMatch(chat.selectedChatUser.UserDisplayPhoneNumber, phone)) {
    const selectedId = pick(chat.selectedChatUser);
    if (selectedId) return selectedId;
  }

  const lists = [
    chat.chatUsers.myChatUsers,
    chat.chatUsers.mySnoozedChatUsers,
    chat.chatUsers.notMyChatUsers,
    chat.chatUsers.unassignedChatUsers,
    chat.chatUsers.botsChatUsers,
    chat.chatUsers.allChatUsers,
    chat.chatUsers.customChatUsers,
    chat.chatUsers.completedChatUsers,
  ];
  for (const list of lists) {
    const found = list.find((u) => phonesMatch(u.UserDisplayPhoneNumber, phone));
    const id = pick(found);
    if (id) return id;
  }
  return null;
}

/**
 * Emergency fallback: fetch open session from contacts API when Redux has no SessionId.
 * Normal mutations should use getSessionIdFromStore() only.
 */
export async function resolveActiveSessionIdForPhone(phone: string): Promise<string | null> {
  const normalized = normalizePhoneForChatUserApi(phone);
  if (!normalized) return null;

  //* חוזה מנוע הסינון בנוד: קבוצות conditions + אופרטור 'eq' (לא 'equals'!).
  //* המנוע החדש משמיט בשקט אופרטורים לא מוכרים — 'equals' החזיר את כל אנשי
  //* הקשר של החברה ושומר ההתאמה החזיר null, מה ששבר את ה-fallback הזה.
  const requestBody = {
    filters: [{ conditions: [{ field: 'phone', operator: 'eq', value: normalized }] }],
    limit: 1,
    offset: 0,
  };
  const contacts = (await fetcher(
    'post',
    ['/api/services/getContacts', { data: requestBody }],
    'servernode',
  )) as ContactResponse[] | null;

  const contact = contacts?.[0];
  const ls = contact?.lastSession;
  if (!ls?.id || typeof ls.id !== 'string') return null;

  const sessionPhone = normalizePhoneForChatUserApi(ls.phoneNumber ?? contact?.phone ?? '');
  if (sessionPhone && sessionPhone !== normalized) return null;

  if (ls.status !== 'open') return null;

  return ls.id.trim() || null;
}

/** Redux SessionId, or getContacts fallback when state is empty. */
export async function resolveSessionIdForMutation(phone: string): Promise<string | null> {
  return getSessionIdFromStore(phone) ?? (await resolveActiveSessionIdForPhone(phone));
}

/**
 * PUT /api/sessions/:id  (.NET Server)
 * Update flow_status_code (1..6). Service auto-handles open/closed flips and
 * SESSION_CLOSED / SESSION_REOPENED log rows.
 */
export async function updateSessionFlowStatus(
  sessionId: string,
  flowStatusCode: number,
  snoozeEndTime?: string | null,
): Promise<UpdateSessionResponse | null> {
  const body: Record<string, unknown> = { flowStatusCode };
  if (snoozeEndTime !== undefined) body.snoozeEndTime = snoozeEndTime;

  const data = (await fetcher(
    'put',
    [`/api/sessions/${sessionId}`, { data: body }],
    'app',
  )) as UpdateSessionResponse | null;
  return data;
}

/**
 * PUT /api/sessions/:id  (.NET Server)
 * Update manager_id (UUID or null to unassign).
 *
 *   - null → null  : noop
 *   - null → X     : first assign → flow becomes 2 (assigned)
 *   - X    → Y     : reassign → MANAGER_CHANGED only, no flow change
 *   - X    → null  : unassign → flow=1 (unassigned), session stays open
 */
export async function updateSessionAssignee(
  sessionId: string,
  managerId: string | null,
): Promise<UpdateSessionResponse | null> {
  const data = (await fetcher(
    'put',
    [`/api/sessions/${sessionId}`, { data: { managerId } }],
    'app',
  )) as UpdateSessionResponse | null;
  return data;
}

/**
 * PUT /api/sessions/:id  (.NET Server)
 * Toggle the "open" / "closed" boolean (translated to flow 6 / 1 + log rows).
 */
export async function updateSessionOpenStatus(
  sessionId: string,
  status: 'open' | 'closed',
): Promise<UpdateSessionResponse | null> {
  const data = (await fetcher(
    'put',
    [`/api/sessions/${sessionId}`, { data: { status } }],
    'app',
  )) as UpdateSessionResponse | null;
  return data;
}

// =============================================================================
// Queries — SessionFlowLog
// =============================================================================

/**
 * GET /api/sessions/:id/log  (server-nodejs)
 * Returns all log rows for a single session, ordered chronologically (asc).
 */
export async function getSessionLog(
  sessionId: string,
): Promise<{ success: boolean; data: SessionFlowLogEntry[] } | null> {
  const data = (await fetcher(
    'get',
    `/api/sessions/${sessionId}/log`,
    'servernode',
  )) as { success: boolean; data: SessionFlowLogEntry[] } | null;
  return data;
}

/**
 * GET /api/sessions/log  (server-nodejs)
 * Returns log rows across the company with filters + pagination.
 */
export async function getSessionsLog(
  query: GetSessionsLogQuery = {},
): Promise<GetSessionsLogResponse | null> {
  const data = (await fetcher(
    'get',
    [`/api/sessions/log`, { data: query }],
    'servernode',
  )) as GetSessionsLogResponse | null;
  return data;
}

// =============================================================================
// Sessions + nested log entries (for conversations-log page)
// =============================================================================

export type SessionWithLogs = {
  id: string;
  company_id: string;
  phone_number: string | null;
  status: 'open' | 'closed';
  first_message: string | null;
  last_message: string | null;
  subject: string | null;
  conversation_topic: string | null;
  conversation_type: string | null;
  ticket_number: string | null;
  serial_number: number | null;
  flow_status_code: number | null;
  flow_status: string | null;
  manager_id: string | null;
  manager_name: string | null;
  date_start: string;
  date_end: string | null;
  duration_minutes: number | null;
  /** Joined from ChatUsers via phone_number+company_id (manually-set display name). */
  set_chat_user_name: string | null;
  /** Joined from ChatUsers — primary display name. */
  full_name: string | null;
  /** Joined from ChatUsers — composed display name (fallback). */
  composed_name: string | null;
  /** Joined from ChatUsers — contact full name (fallback). */
  contact_full_name: string | null;
  logEntries: Array<{
    id: string;
    session_id: string;
    event_type: SessionFlowLogEventType;
    old_status_code: number | null;
    new_status_code: number | null;
    old_manager_id: string | null;
    new_manager_id: string | null;
    source: SessionFlowLogSource;
    actor_user_id: string | null;
    metadata_json: string | null;
    created_at: string;
    /** Joined from AspNetUsers via actor_user_id (Udid). */
    actor_user_name?: string | null;
    /** Joined from AspNetUsers via old_manager_id (Udid). */
    old_manager_name?: string | null;
    /** Joined from AspNetUsers via new_manager_id (Udid). */
    new_manager_name?: string | null;
  }>;
};

export type GetSessionsWithLogsResponse = {
  success: boolean;
  data: SessionWithLogs[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

/**
 * GET /api/sessions/with-logs  (server-nodejs)
 * Returns sessions for the company with full SessionFlowLog history nested.
 * Backend filters: status, phoneNumber. Pagination via page/pageSize.
 */
export async function getSessionsWithLogs(
  query: {
    page?: number;
    pageSize?: number;
    status?: 'open' | 'closed';
    /** התאמה מדויקת למספר — ליומן השיחות של איש קשר בודד. */
    phoneNumber?: string;
    /** חיפוש חופשי: שם איש קשר או מספר חלקי (יומן השיחות הכללי). */
    search?: string;
  } = {},
): Promise<GetSessionsWithLogsResponse | null> {
  const data = (await fetcher(
    'get',
    [`/api/sessions/with-logs`, { data: query }],
    'servernode',
  )) as GetSessionsWithLogsResponse | null;
  return data;
}
